'use client'

import React, { useEffect, useRef, useState } from 'react'
import { clsx } from 'clsx'
import { createPortal } from 'react-dom'
import { Icon } from '../Icon'
import { Toast, SaveStatus } from '@/components/Toast'
import styles from './Popup.module.scss'
import { isBrowser } from '@/utils/app/detect'

export interface PopupProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode
  className?: string
  title?: string
  setIsOpen: (value: boolean) => void
  shareUrl?: string
}

const Popup = ({ children, className = '', title, setIsOpen, shareUrl }: PopupProps) => {
  const popupRef = useRef<HTMLDialogElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const shareButtonRef = useRef<HTMLButtonElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  const [toastOpen, setToastOpen] = useState(false)
  const [status, setStatus] = useState<SaveStatus>('success')
  const [message, setMessage] = useState('Link copied to clipboard')

  useEffect(() => {
    // Store the active element before focusing this popup
    previousFocusRef.current = document.activeElement as HTMLElement

    // Focus the close button of this popup - ensure it exists first
    if (closeButtonRef.current) {
      setTimeout(() => {
        if (closeButtonRef.current) {
          closeButtonRef.current.focus()
        }
      }, 50)
    }

    return () => {
      // Check if any other popups are still open
      const otherPopupDialogs = document.querySelectorAll('dialog[open]')

      if (otherPopupDialogs.length > 0) {
        // If there are other open popups, find the first focusable element in the most recently opened one
        // (Typically would be the last one in the DOM)
        const lastPopup = otherPopupDialogs[otherPopupDialogs.length - 1]
        const focusableElement = lastPopup.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ) as HTMLElement

        if (focusableElement) {
          focusableElement.focus()
          return
        }
      }

      // If no other popups, return focus to previous element
      if (previousFocusRef.current && 'focus' in previousFocusRef.current) {
        previousFocusRef.current.focus()
      }
    }
  }, [])

  useEffect(() => {
    if (!popupRef.current) return

    const popupElement = popupRef.current

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false)
        e.preventDefault()
        return
      }

      if (e.key === 'Tab') {
        const focusableElements = popupElement.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        )

        if (focusableElements.length === 0) return

        const firstElement = focusableElements[0] as HTMLElement
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement

        if (e.shiftKey && document.activeElement === firstElement) {
          lastElement.focus()
          e.preventDefault()
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          firstElement.focus()
          e.preventDefault()
        }
      }
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (popupElement === event.target && contentRef.current && !contentRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    popupElement.addEventListener('click', handleClickOutside)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      popupElement.removeEventListener('click', handleClickOutside)
    }
  }, [setIsOpen])

  const handleClose = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsOpen(false)
  }

  const handleShare = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()

    if (shareUrl) {
      navigator.clipboard
        .writeText(shareUrl)
        .then(() => {
          setMessage('Url in die Zwischenablage kopiert')
          setStatus('success')
          setToastOpen(true)
          setTimeout(() => setToastOpen(false), 2000)
        })
        .catch((err) => {
          console.error('Failed to copy: ', err)
          setMessage('Url konnte nicht kopiert werden')
          setStatus('error')
          setToastOpen(true)
          setTimeout(() => setToastOpen(false), 2000)
        })
    }
  }

  return (
    <Modal>
      <dialog className={styles.wrapper} ref={popupRef} open>
        <div ref={contentRef} className={clsx(styles.popup, className)}>
          <div className={styles.header}>
            {title && <div className={styles.title}>{title}</div>}
            <div className={styles.actions}>
              {shareUrl && (
                <button
                  ref={shareButtonRef}
                  aria-label="Share"
                  className={styles['button-share']}
                  onClick={handleShare}
                >
                  <Icon name="share" title="Share" />
                </button>
              )}
              <button
                ref={closeButtonRef}
                aria-label="Popup schließen"
                className={styles['button-close']}
                onClick={handleClose}
              >
                <Icon name="close" title="Popup schließen" />
              </button>
            </div>
          </div>
          <div className={styles.content}>{children}</div>
        </div>
        {toastOpen && <Toast status={status} message={message} />}
      </dialog>
    </Modal>
  )
}

const Modal = ({ children }: { children: React.ReactNode }) => {
  const modalRoot = isBrowser() ? document.getElementById('modal-root') : null
  if (modalRoot) {
    return createPortal(children, modalRoot)
  }
  return null
}

export default Popup
